import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineUniqueFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { Contacts } from "@/src/gql/server/graphql";
import {
  coreContactFields,
  companyContactFields,
  locationContactFields,
  statusContactFields,
  metadataContactFields,
  extendedContactFields,
  contactActions,
  contactDetailActions,
  contactBulkActions,
  contactSavedViews,
  contactCustomFilterHooks,
  contactFeatures,
} from "../config";
import { ENHANCED_CONTACT_DETAIL_CONFIG } from "./ContactDetailConfig";

/** Logical order for create contact form: identity → company → location → status → notes/tags */
const CREATE_CONTACT_FIELD_ORDER: string[] = [
  "name",
  "email",
  "phone",
  "title",
  "company_id",
  "address",
  "city",
  "state",
  "zip_code",
  "country",
  "status",
  "source",
  "tags",
  "is_active",
  "lifecycle_stage",
  "notes",
  "email_aliases",
  "phone_aliases",
  "social_links",
  "custom_fields",
];

/**
 * Progressive-disclosure section layout for the Create Contact form.
 *
 * See plan `the-log-activity-modal-enumerated-whisper.md`. Required identity
 * fields stay visible; relationship/location/channels/notes/custom fields
 * collapse behind accordion triggers to reduce visual overwhelm.
 */
const CREATE_CONTACT_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["name", "email", "status"],
  },
  {
    id: "relationships",
    kind: "collapsible",
    summary: "Link to a company",
    fieldNames: ["company_id"],
  },
  {
    id: "contact-channels",
    kind: "collapsible",
    summary: "Phone, title, email/phone aliases, social links",
    fieldNames: ["phone", "phone_aliases", "title", "email_aliases", "social_links"],
  },
  {
    id: "location",
    kind: "collapsible",
    fieldNames: ["address", "city", "state", "zip_code", "country"],
  },
  {
    id: "classification",
    kind: "collapsible",
    summary: "Source, tags, lifecycle, active",
    fieldNames: ["source", "tags", "lifecycle_stage", "is_active"],
  },
  {
    id: "description",
    kind: "collapsible",
    title: "Notes",
    summary: "Free-form notes about this contact",
    fieldNames: ["notes"],
  },
  {
    id: "custom",
    kind: "collapsible",
    fieldNames: ["custom_fields"],
  },
];

export const ENHANCED_CONTACTS_CONFIG: SimpleTableConfig<Contacts> = {
  entity: "contacts",
  displayName: "Contacts",
  statusFieldId: "status",
  defaultPageSize: 10,
  boardViewPageSize: 100,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,

  createFormFieldOrder: CREATE_CONTACT_FIELD_ORDER,
  createFormSections: CREATE_CONTACT_FORM_SECTIONS,

  /** Default table column order: who → role → account → reach → status → recency */
  tableColumnOrder: [
    "name",
    "title",
    "company_name",
    "email",
    "phone",
    "status",
    "last_contacted_at",
  ],

  /** Card view: identity → relationship → how to reach them */
  cardFaceFieldOrder: [
    "name",
    "status",
    "company_name",
    "email",
    "phone",
    "title",
  ],

  mobileDetailSections: [
    { id: "basic", label: "Basic Info" },
    { id: "company", label: "Company" },
    { id: "location", label: "Location" },
    { id: "status", label: "Status & Tracking" },
    { id: "extended", label: "Notes & Links" },
    { id: "metadata", label: "Metadata" },
  ],

  fields: defineUniqueFieldConfigMap({
    ...coreContactFields,
    ...companyContactFields,
    ...locationContactFields,
    ...statusContactFields,
    ...metadataContactFields,
    ...extendedContactFields,
  }),

  actions: contactActions,

  // Add detail page actions for table rows
  detailActions: contactDetailActions,

  bulkActions: contactBulkActions,

  features: contactFeatures,

  savedViews: contactSavedViews,

  // Custom filter hooks for saved views that require special filtering logic
  customFilterHooks: contactCustomFilterHooks,
};
